<script setup lang="ts">
const props = defineProps<{
  items: { label: string; content: string }[]
}>()

// splitBold leaves the **bold** runs at odd indexes, so they render as <strong> through text
// interpolation, never through v-html.

// Native <details> keeps every answer in the server-rendered HTML without a `hidden` attribute,
// so crawlers that skip hidden elements or run no JS still read the answers, and the FAQ opens
// without JS. A shared `name` lets only one item per accordion stay open.
const name = useId()
</script>

<template>
  <div class="accordion flex flex-col gap-2">
    <details v-for="(item, index) in props.items" :key="index" :name="name" class="group block rounded-2xl text-white">
      <summary
        class="flex w-full cursor-pointer list-none items-center justify-between gap-4 rounded-lg bg-white/10 px-4 py-2 text-left font-medium text-white hover:bg-white/20 focus:outline-hidden focus-visible:ring-3 focus-visible:ring-purple-500/75 [&::-webkit-details-marker]:hidden"
      >
        <h3 class="text-lg">{{ item.label }}</h3>
        <span class="w-5 shrink-0">
          <Icon name="fa6-solid:chevron-down" class="h-5 w-5 transition-transform duration-200 group-open:rotate-180" />
        </span>
      </summary>

      <!-- pre-line so answers can be written as paragraphs; runs of spaces still collapse -->
      <div class="px-4 py-4 whitespace-pre-line">
        <template v-for="(part, i) in splitBold(item.content)" :key="i">
          <strong v-if="i % 2">{{ part }}</strong>
          <template v-else>{{ part }}</template>
        </template>
      </div>
    </details>
  </div>
</template>

<style scoped>
/* Browsers that can animate to `auto` slide the answer open and shut; the rest just toggle it. */
.accordion {
  interpolate-size: allow-keywords;
}

details::details-content {
  block-size: 0;
  overflow: hidden;
  transition:
    block-size 200ms ease-out,
    content-visibility 200ms ease-out allow-discrete;
}

details[open]::details-content {
  block-size: auto;
}
</style>
